/**
 * PEOPLE & ACCESS, read and written through the database.
 *
 * The email change is the one worth reading carefully. It is TWO updates and
 * only one of them is ours:
 *
 *   1. supabase.auth.updateUser({ email }) sends a confirmation to the NEW
 *      address. auth.users.email does not change until that link is clicked.
 *   2. When it is, the trigger on auth.users updates admin_users.email in the
 *      same transaction (migration 54).
 *
 * So this module cannot write admin_users.email, and does not try. The
 * alternative — the Supabase Admin API — changes it instantly and needs the
 * service role key, which bypasses every policy in the database and therefore
 * cannot be in this bundle. Proving you can receive mail at the address you are
 * claiming is not the cost of that choice; it is the better half of it.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';
import { readableEmailError } from './email';

const fail = (error) => ({ data: null, error });

/**
 * The three calls below all hand work to Supabase's mail transport, and all
 * three used to relay its raw wording. "Error sending the change email" is the
 * one the founder pressed a button and got: true, and useless.
 */
const mailFail = (error, fallback) => fail(readableEmailError(error, fallback));

export async function fetchStaffDirectory() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_staff_directory');
  if (error) return fail(error.message || 'The staff directory could not be loaded.');
  return { data: data || [], error: null };
}

export async function fetchGrantablePermissions() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_grantable_permissions');
  if (error) return fail(error.message || 'The permission list could not be loaded.');
  return { data: data || [], error: null };
}

/**
 * The five departments admin_users_role_check permits.
 *
 * A label for which part of Orchid somebody works in — NOT authority. Script 48
 * is explicit: "admin_users.role is NOT what makes somebody super: holding
 * 'all' is." What a person may do is the permission grant, which is a separate
 * and separately logged act.
 */
export const STAFF_DEPARTMENTS = [
  { value: 'compliance',         label: 'Compliance' },
  { value: 'leads',              label: 'Leads' },
  { value: 'provider_servicing', label: 'Provider Servicing' },
  { value: 'dsr_processing',     label: 'DSR Processing' },
  { value: 'super_admin',        label: 'Super Admin' },
];

/**
 * What is wrong with this invitation, before it costs a round trip.
 *
 * The domain rule is the database's — trg_admin_email_domain has enforced it
 * since script 48 — and it is restated here so the answer arrives while the
 * field is still on screen rather than as a trigger exception.
 */
export function staffInviteProblem({ fullName, email, role } = {}) {
  if (String(fullName || '').trim().length < 2) return 'Who is this? A first and last name.';
  const e = String(email || '').trim().toLowerCase();
  if (!e) return 'They need an Orchid address to sign in with.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return 'That is not an email address.';
  if (!e.endsWith('@orchidholdings.co.za')) {
    return 'Staff sign in with an @orchidholdings.co.za address. '
         + 'Staff identity is deliberately separate from user identity.';
  }
  if (!STAFF_DEPARTMENTS.some((d) => d.value === role)) {
    return 'Which part of Orchid do they work in?';
  }
  return null;
}

/**
 * Add somebody to the staff.
 *
 * This creates an INVITATION, not an account, and the distinction is worth
 * keeping in the wording the screen uses. A portal cannot mint a credential —
 * that needs the service role, which is not in this bundle and should not be —
 * so the row waits with no login attached until that person signs up with the
 * address. Permissions are a separate call for the same reason canon gives:
 * every grant needs a named human accountable for it, and inviting somebody is
 * not the same act as empowering them.
 */
export async function inviteStaffMember({ fullName, email, role, jobTitle, reason }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const problem = staffInviteProblem({ fullName, email, role });
  if (problem) return fail(problem);

  const { data, error } = await supabase.rpc('fn_invite_staff_member', {
    p_full_name: fullName.trim(),
    p_email: email.trim().toLowerCase(),
    p_role: role,
    p_job_title: jobTitle?.trim() || null,
    p_reason: reason?.trim() || null,
  });
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('Adding staff is not available on this database yet. '
                + 'If you are the administrator, script 66 has not been run.');
    }
    return fail(error.message || 'That staff member could not be added.');
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return fail('Nothing was saved.');
  return { data: row, error: null };
}

export async function updateStaffMember({ id, reason, fullName = null, jobTitle = null, phone = null, status = null }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { error } = await supabase.rpc('fn_update_staff_member', {
    p_id: id, p_reason: reason, p_full_name: fullName,
    p_job_title: jobTitle, p_phone: phone, p_status: status,
  });
  if (error) return fail(error.message || 'That change could not be saved.');
  return { data: true, error: null };
}

/**
 * Remove a staff seat that nobody ever used.
 *
 *   "I should be able to delete a staff user when someone leaves the company."
 *
 * True, and the reason there was no delete is also true: twenty-seven tables
 * reference admin_users and every one of them names the person who did the
 * thing. An audit trail that forgets who approved something is not one.
 *
 * That reasoning only covers somebody who has ACTED. A seat created by mistake,
 * never signed into, that approved nothing, is referenced by no rows at all —
 * deleting it destroys no evidence because there is none, and keeping it forces
 * a real person to look at a permanent list of their own typing errors.
 *
 * fn_withdraw_staff_invitation lets the DATABASE decide which of the two this
 * is: it attempts the delete and reads a foreign key violation as "somebody
 * still names this person". So this function does not need to know the rule,
 * and cannot get it wrong when the twenty-eighth table arrives.
 */
export async function withdrawStaffInvitation({ id, reason }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_withdraw_staff_invitation', {
    p_admin_user_id: id,
    p_reason: String(reason || '').trim(),
  });
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('Removing a staff seat is not available on this database yet. '
                + 'Script 94 has not been run.');
    }
    return fail(error.message || 'That seat could not be removed.');
  }
  return { data: (Array.isArray(data) ? data[0] : data) || null, error: null };
}

export async function setStaffPermissions({ id, permissions, reason }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { error } = await supabase.rpc('fn_set_staff_permissions', {
    p_id: id, p_permissions: permissions, p_reason: reason,
  });
  if (error) return fail(error.message || 'Those permissions could not be set.');
  return { data: true, error: null };
}

/**
 * Start changing your OWN address. Only your own: Supabase's client library
 * changes the signed-in user and nobody else, which happens to be exactly the
 * rule we want. Somebody else's address is changed by that person, from their
 * own session, after they receive the confirmation.
 */
export async function requestEmailChange(newEmail) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  // emailRedirectTo, WITHOUT WHICH SUPABASE SENDS THEM TO THE MARKETING SITE.
  //
  // The founder: "when I send myself a 'change your email address' confirmation
  // email, and I click on the link, it just takes me to the Orchid marketing
  // site. I've been trying to make eddie@orchidholdings.co.za my email address
  // for the longest time." He was doing it from HERE.
  //
  // With this option absent, Supabase builds {{ .ConfirmationURL }} with
  // redirect_to defaulting to the project's SITE URL, which is the marketing
  // site -- not this portal. window.location.origin sends it back to whichever
  // origin actually served the portal.
  //
  // NOTE FOR WHOEVER READS THIS NEXT: this origin must be in Supabase's
  // Redirect URLs allow-list. An unlisted one is SILENTLY IGNORED and falls
  // back to the Site URL -- which reproduces the original bug exactly while
  // looking like the fix did not take.
  const { error } = await supabase.auth.updateUser(
    { email: newEmail.trim().toLowerCase() },
    typeof window !== 'undefined'
      ? { emailRedirectTo: `${window.location.origin}/auth/confirm` }
      : undefined,
  );
  // "The 'Send the confirmation' button doesn't do what it's supposed to do -
  //  it doesn't send the email, error: 'Error sending the change email.'"
  //
  // The request was fine and the send was not. Nothing changed on either
  // address, which is the part a person needs told -- a failure here reads as
  // "did it half-happen?" unless it says otherwise.
  if (error) {
    return mailFail(error, 'That address change could not be started.');
  }
  return { data: true, error: null };
}

/**
 * Send a password-reset link.
 *
 * Works with the anon key, for yourself or for a colleague, because it sends
 * mail to an address rather than revealing anything about it. That is also why
 * the caller must not vary its message by whether the address exists.
 */
export async function sendPasswordReset(email) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
    redirectTo: `${window.location.origin}/reset`,
  });
  if (error) return mailFail(error, 'That reset link could not be sent.');
  return { data: true, error: null };
}
